import React, {useMemo, useState} from 'react'
import {ILeague} from '@api/rank/league/league'
import useFormatDate from '@hook/useFormatDate'
import {useTranslation} from '@lib/intl/useTranslations'
import Text from '@component/Text/Text'
import LeagueIcon from '@component/images/LeagueIcon'
import useFormatNumber from '@hook/useFormatNumber'
import useNavigation from '@hook/useNavigation'
import useWindowSize from '@hook/useWindowSize'
import {IUserRank} from '@api/rank/rank'
import Spinner from '@component/Spinner/Spinner'
import UserThumbnail from '@component/images/UserThumbnail'
import {getNumberColorClass} from '@util/etc'
import IconUp from '@svg/common/ic_btn_up.svg'
import IconDown from '@svg/common/ic_btn_down.svg'
import {useRouter} from 'next/router'
import IconNext from '@svg/common/ic_btn_next.svg'

interface IProps extends ILeague {
    onPress: () => void
    isExpanded: boolean
    isSpinner?: boolean
    expandedRank?: IUserRank[]
}

const PreviousLeagueItem: React.FC<IProps> = ({
    id,
    name,
    icon,
    start_at,
    end_at,
    filter,
    my_league_rank,
    members_count,
    prize_pool,
    onPress,
    isExpanded,
    expandedRank,
    isSpinner,
    start_urls,
}) => {
    const t = useTranslation()
    const router = useRouter()
    const {displayDate} = useFormatDate()
    const {displayNumber, displayPercent} = useFormatNumber()

    const {goLeaderboardLeague} = useNavigation()
    const {isMd} = useWindowSize()

    const [isOpen, setIsOpen] = useState(false)

    const onClickExpandIcon = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e?.cancelable) {
            e.preventDefault()
        }
        e.stopPropagation()
        onPress()
    }

    const onClickContainer = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isExpanded && !isMd) {
            onClickExpandIcon(e)
            return
        }
        goLeaderboardLeague(id)
    }

    const onClickLeagueDetails = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e?.cancelable) {
            e.preventDefault()
        }
        e.stopPropagation()

        if (router?.locale === 'ko') {
            if (start_urls?.ko?.includes('https://event.leagueoftraders.io/')) {
                window.open(start_urls?.ko)
            } else {
                location.href = start_urls?.ko
            }
        } else {
            if (start_urls?.en?.includes('https://event.leagueoftraders.io/')) {
                window.open(start_urls?.en)
            } else {
                location.href = start_urls?.en
            }
        }
    }

    const onClickSeeLeaderboard = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e?.cancelable) {
            e.preventDefault()
        }
        e.stopPropagation()
        goLeaderboardLeague(id)
    }

    const getExpandStyle = `border-indigo dark:border-dark_indigo bg-indigo_tint02 dark:bg-dark_indigo_tint02 shadow-normal`

    const getRankMedalStyle = idx => {
        switch (idx) {
            case 0:
                return 'bg-[#FFCA40]'
            case 1:
                return 'bg-[#CDCDCD]'
            case 2:
                return 'bg-[#D8926B]'
        }
    }

    const isCheckDetail = useMemo(() => {
        if (router?.locale === 'ko') {
            return start_urls?.ko ? true : false
        } else {
            return start_urls?.en ? true : false
        }
    }, [router, start_urls])

    return (
        <div
            key={id}
            onClick={onClickContainer}
            className={`py-[20px] px-[30px] rounded-[5px] cursor-pointer border ${
                isExpanded
                    ? getExpandStyle
                    : 'border-gray07 dark:border-dark_gray07 hover:bg-indigo_tint01 dark:hover:bg-dark_indigo_tint01 '
            }`}>
            <div className={'flex gap-x-[5px] justify-between items-center w-full'}>
                <LeagueIcon src={icon} size={30} />
                <div className={'ml-[15px] flex-1 flex-grow'}>
                    <Text className={'text-ti1 text-indigo dark:text-dark_indigo'}>{name}</Text>
                    <Text className={'text-body3 text-gray02 dark:text-dark_gray02'}>
                        {displayDate(end_at, t('dateFormat.dateAndTime'))}
                    </Text>
                </div>
                <div className={'flex flex-1 sm:hidden'}>
                    <div className={'px-2 text-center flex-1'}>
                        <Text className={'text-body3 text-gray03 dark:text-dark_gray03'}>
                            {t('leaderboard.leagueList.previousLeagues.item.myRanking.header.title')}
                        </Text>
                        <Text className={'text-body1 text-gray02 dark:text-dark_gray02'}>
                            {my_league_rank ? `#${my_league_rank}` : '-'}
                        </Text>
                    </div>
                    <div className={'px-2 text-center flex-1'}>
                        <Text className={'text-body3 text-gray03 dark:text-dark_gray03'}>
                            {t('leaderboard.leagueList.previousLeagues.item.participants.header.title')}
                        </Text>
                        <Text className={'text-body1 text-gray02 dark:text-dark_gray02'}>
                            {displayNumber(members_count)}
                        </Text>
                    </div>
                    <div className={'px-2 text-center flex-1'}>
                        <Text className={'text-body3 text-gray03 dark:text-dark_gray03'}>
                            {t('leaderboard.leagueList.previousLeagues.item.prizePool.header.title')}
                        </Text>
                        <Text className={'text-body1 text-gray02 dark:text-dark_gray02'}>{prize_pool}</Text>
                    </div>
                    <div className={'flex items-center md:hidden'}>
                        <div className={'p-[3px]'} onClick={onClickExpandIcon}>
                            {isExpanded ? (
                                <IconUp className={'w-[18px] fill-gray01 dark:fill-dark_gray01'} />
                            ) : (
                                <IconDown width={18} className={'fill-gray01 dark:fill-dark_gray01'} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isExpanded && (
                <>
                    <div
                        className={
                            'mt-[20px] py-[10px] px-[40px] bg-white dark:bg-bg_dark_white02 bg-opacity-50 rounded-[5px]'
                        }>
                        {isSpinner ? (
                            <div className={'flex items-center h-full min-h-[42px] justify-center'}>
                                <Spinner visible={true} />
                            </div>
                        ) : (
                            <div className={'flex items-center gap-x-[10px]'}>
                                {expandedRank?.map((user, idx) => (
                                    <div key={user?.id} className={'flex items-center gap-x-[5px] flex-1'}>
                                        <div className={'relative'}>
                                            <div
                                                className={`border border-white rounded-full w-[16px] h-[16px] absolute top-0 left-[-6px] text-center ${getRankMedalStyle(
                                                    idx,
                                                )}`}>
                                                <Text className={'text-black opacity-60 text-ti4'}>{idx + 1}</Text>
                                            </div>
                                            <UserThumbnail src={user?.thumbnail} size={30} />
                                        </div>
                                        <div>
                                            <Text className={'text-body2 text-gray01 dark:text-dark_gray01'}>
                                                {user?.name}
                                            </Text>
                                            <Text className={`text-ti2 ${getNumberColorClass(user?.score)}`}>
                                                {displayPercent(user?.score)}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={'mt-[20px] flex items-center justify-end gap-x-[20px]'}>
                        {isCheckDetail && (
                            <div className={'flex items-center gap-x-[5px]'} onClick={onClickLeagueDetails}>
                                <Text className={'text-ti3 text-gray02 dark:text-dark_gray02'}>
                                    {t('leaderboard.leagueList.previousLeagues.leagueDetail')}
                                </Text>
                                <IconNext className={'w-[12px] fill-gray02 dark:fill-dark_gray02'} />
                            </div>
                        )}
                        <div className={'flex items-center gap-x-[5px]'} onClick={onClickSeeLeaderboard}>
                            <Text className={'text-ti3 text-gray02 dark:text-dark_gray02'}>
                                {t('leaderboard.leagueList.previousLeagues.goLeaderboard')}
                            </Text>
                            <IconNext className={'w-[12px] fill-gray02 dark:fill-dark_gray02'} />
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default PreviousLeagueItem
